/**
 * Catalogue des produits SEmply, servi par GET /products — la source de vérité
 * est la table OidcClient, éditée depuis le back-office (noms, descriptions,
 * URLs, statut « lancé », ordre). Plus rien en dur ici.
 *
 * Le résultat est mémorisé pour la session de page : le portail est une petite
 * SPA, un rechargement suffit à voir un changement du back-office.
 */
import { urlProduitSure, type ProductInfo } from '@authsemply/shared';
import { api } from './api';

let cache: Promise<ProductInfo[]> | null = null;

/**
 * SEmplyApp, destination de repli quand le catalogue n'a aucun produit ou ne
 * répond pas (02/10/2026). Exportée par deploy.sh depuis `APP_URL` ; absente,
 * le repli reste la page compte.
 */
export const APP_URL =
  ((import.meta.env.VITE_APP_URL as string | undefined) ?? '').trim().replace(/\/$/, '') || null;

export function loadProducts(): Promise<ProductInfo[]> {
  // Les adresses sont aussi filtrées ici (https seulement) : elles finissent
  // en lien et en redirection.
  cache ??= api
    .get<ProductInfo[]>('/products')
    .then((liste) => liste.filter((p) => urlProduitSure(p.url)))
    .catch(() => {
      cache = null; // erreur réseau : ne pas mémoriser un catalogue vide
      return [];
    });
  return cache;
}

/**
 * Pages du portail où l'on revient après une connexion (`?suite=`) : une
 * liste FERMÉE, jamais un chemin libre — le portail ne doit pas devenir un
 * redirecteur. Le cas d'usage : la page de suppression du compte, où l'on
 * arrive depuis semply.fr et où une connexion récente est exigée (01/10/2026).
 */
const SUITES_AUTORISEES = ['/compte', '/compte/suppression', '/compte/mot-de-passe', '/espace'];

/**
 * Retour sur la page d'invitation après connexion (04/10/2026) : le jeton est
 * le seul paramètre admis, au format exact de `generateToken` (base64url). Un
 * chemin libre ferait du portail un redirecteur ; ce motif n'en est pas un.
 */
const SUITE_INVITATION = /^\/invitation\?token=[A-Za-z0-9_-]{16,256}$/;

/**
 * Création et modification de dossier (11/10/2026) : écrans du portail où un
 * produit envoie l'utilisateur, éventuellement avant connexion. Chemins et
 * paramètres FERMÉS (UUID, nom de produit, adresse de retour encodée) — le
 * retour vers un produit est, lui, revérifié par l'API (CORS_ORIGINS).
 */
const SUITE_DOSSIER =
  /^\/(bienvenue|abonnements|dossiers\/nouveau|dossiers\/creation\/[0-9a-f-]{36}|dossiers\/[0-9a-f-]{36})(\?[A-Za-z0-9_.~%&=+-]{0,600})?(#[a-z0-9_]{1,120})?$/i;

export function suiteInterne(params?: URLSearchParams): string | null {
  const suite = params?.get('suite');
  if (!suite) return null;
  if (SUITES_AUTORISEES.includes(suite)) return suite;
  if (SUITE_DOSSIER.test(suite)) return suite;
  return SUITE_INVITATION.test(suite) ? suite : null;
}

/**
 * Destination après une connexion au portail SANS flux OIDC à reprendre :
 * la page demandée (`?suite=`, liste fermée) ; sinon un seul produit lancé →
 * on y va sans poser de question ; plusieurs → écran de choix ; aucun (ou
 * catalogue injoignable) → SEmplyApp (`APP_URL`), à défaut la page compte.
 */
export async function postLoginDestination(
  params?: URLSearchParams,
): Promise<{ external: string } | { internal: string }> {
  const suite = suiteInterne(params);
  if (suite) return { internal: suite };
  // Première connexion (11/10/2026) : « Créer votre dossier ou accéder aux
  // applications ? », tant que l'utilisateur n'y a pas répondu et n'a aucun dossier.
  const accueil = await api
    .get<{ afficher: boolean }>('/espace/dossiers/accueil')
    .catch(() => ({ afficher: false }));
  if (accueil.afficher) return { internal: '/bienvenue' };
  const products = await loadProducts();
  if (products.length === 1) return { external: products[0].url };
  if (products.length > 1) return { internal: '/produits' };
  return APP_URL ? { external: APP_URL } : { internal: '/compte' };
}
